import RequestVariablesToggle from '../RequestVariablesToggle';
import RequestHistoryButton from '../git/RequestHistoryButton';
import HttpMethodPicker from '../ui/HttpMethodPicker';
import { Send, ChevronRight, Clock, X, Save, Trash2, ChevronDown, Download } from 'lucide-react';
import SyntaxHighlightInput, { TRIGGER_BRACES } from '../ui/SyntaxHighlightInput';
import { stopHttpEventStream } from '../../utils/httpEventStreams';
import { formatHistoryTime } from '../../utils/formatHistoryTime';
import { splitUrlAndParams } from '../../utils/urlParams';
import { makeKvRowId } from '../../utils/kvRows';
import { SUGGEST_VARIABLES } from '../../utils/variableSuggestions';

/** Breadcrumb toolbar and URL bar with Send / History (plain render function, called where the markup was). */
export function renderRequestHeader({
  collectionName, t, nameSlotWidth, editingRequestName, nameInputRef, name, setName, emitDataChange,
  method, url, headers, params, cookies, body, bodyType, auth, commitRequestNameEdit,
  nameSnapshotRef, setEditingRequestName, readOnly, breadcrumbDisplayLabel, historyPreview,
  timeFormat, exitHistoryPreview, initialData, saveRequest, getMethodTextColor, editable, setMethod,
  urlInputRef, setUrl, tabFieldsRef, setParams, sendHttpRequest, handleUrlPaste, urlImportNonce,
  selectedEnvironment, workspaceVariables, globalVariables, collectionVariables, folderVariables,
  onVariableUpdate, currentWorkspaceId, historyDropdownTriggerRef, setShowHistoryDropdown,
  showHistoryDropdown, historyDropdownPosition, historyDropdownRef, requestHistory,
  onClearRequestHistory, requestHistoryKey, activeHistoryId, enterHistoryPreview, isEventStreaming,
  loading, sendDropdownTriggerRef, setShowSendDropdown, showSendDropdown, sendDropdownPosition,
  sendDropdownRef, downloadResponse,
}) {
  return (
    <div className="flex-shrink-0 bg-white border-b border-gray-200 px-5 py-3 shadow-sm">
      <div className="editor-header-toolbar editor-header-toolbar--request">
        <div className="flex items-center min-w-0 flex-1 gap-2 text-xs">
          <Send size={12} className="text-green-600 flex-shrink-0" aria-hidden />
          <span className="text-gray-500 truncate cursor-pointer" title={collectionName || undefined}>
            {collectionName ?? t('request.noCollection')}
          </span>
          <ChevronRight size={14} strokeWidth={2} className="text-gray-400 flex-shrink-0 opacity-90" aria-hidden />
          <span
            className="inline-flex items-center justify-start min-w-0 max-w-full"
            style={{
              width: nameSlotWidth,
              maxWidth: 'min(28rem, 100%)',
            }}
          >
            {editingRequestName ? (
              <input
                ref={nameInputRef}
                type="text"
                value={name}
                placeholder={t('request.newRequest')}
                onChange={(e) => {
                  const next = e.target.value;
                  setName(next);
                  emitDataChange({ name: next, method, url, headers, params, cookies, body, bodyType, auth });
                }}
                onBlur={commitRequestNameEdit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitRequestNameEdit();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    const prev = nameSnapshotRef.current;
                    setName(prev);
                    emitDataChange({ name: prev, method, url, headers, params, cookies, body, bodyType, auth });
                    setEditingRequestName(false);
                  }
                }}
                className="request-breadcrumb-name-input request-breadcrumb-name-slot-inner w-full min-w-0 text-gray-900 font-semibold max-w-full"
              />
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (readOnly) return;
                  nameSnapshotRef.current = name;
                  setEditingRequestName(true);
                }}
                className="request-breadcrumb-name-slot-inner w-full min-w-0 font-semibold text-gray-900 truncate text-left bg-transparent border-none cursor-text m-0 hover:opacity-90 max-w-full"
                title={t('request.editRequestName')}
              >
                {breadcrumbDisplayLabel}
              </button>
            )}
          </span>
          {historyPreview && (
            <span className="request-history-chip" title={new Date(historyPreview.timestamp).toLocaleString()}>
              <Clock size={11} className="flex-shrink-0" aria-hidden />
              <span className="request-history-chip__label">{formatHistoryTime(historyPreview.timestamp, timeFormat)}</span>
              <button
                type="button"
                className="request-history-chip__close"
                onClick={exitHistoryPreview}
                title={t('common.close')}
                aria-label={t('common.close')}
              >
                <X size={12} strokeWidth={2.5} />
              </button>
            </span>
          )}
        </div>
          <RequestHistoryButton requestId={initialData?.originalId} requestName={name} />
          <RequestVariablesToggle />
          {!readOnly && (
        <button
          type="button"
          onClick={saveRequest}
          className="request-header-save inline-flex items-center gap-2 px-3 py-1 rounded-md bg-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors text-xs font-medium flex-shrink-0"
          title={t('request.saveRequest')}
        >
          <Save size={14} strokeWidth={2} />
          <span className="hidden sm:inline">{t('request.save')}</span>
        </button>
          )}
      </div>
    
      {/* Compact request bar: method + URL (grouped) | gap | split Send */}
      <div className="flex items-stretch gap-2 min-w-0 w-full">
        <div className="request-url-field-group url-container flex-1 min-w-0 rounded-lg border border-gray-200 overflow-hidden bg-white">
          <div className="request-url-bar__method-wrap relative flex flex-shrink-0 items-stretch border-r border-gray-200 bg-white">
            <HttpMethodPicker
              key={initialData?.id || 'request-tab'}
              value={method}
              getMethodColor={getMethodTextColor}
              onChange={(m) => {
                if (!editable) return;
                setMethod(m);
                emitDataChange({ name, method: m, url, headers, params, cookies, body, bodyType, auth });
              }}
            />
          </div>
    
          <div className="url-container__url min-w-0 flex-1 flex flex-col justify-center bg-white">
            <SyntaxHighlightInput
              placeholder={t('request.enterUrl')}
              value={url}
              inputRef={urlInputRef}
              disabled={readOnly}
              expandOnOverflow
              selectionToVariable
              // Typing `{{` in the URL offers what you have. The picker is
              // opt-in per field (it defaults to the KV inputs, and the URL is
              // not one), so it had none at all. Variables ONLY: a generated
              // first name in a URL is nobody's intent (owner, 23.09.2026).
              showDynamicVarPicker
              suggest={SUGGEST_VARIABLES}
              suggestTrigger={TRIGGER_BRACES}
              onChange={(e) => {
                if (!editable) return;
                const value = e.target.value;
                setUrl(value);
                // Live sync URL→params on every keystroke (Postman-style).
                // Use position-based _id reuse so React never unmounts/remounts
                // param inputs — new ids cause remount → focus stolen from params.
                const { params: parsed } = splitUrlAndParams(value);
                const currentParams = tabFieldsRef.current.params;
                let newParamRows;
                if (parsed.length === 0) {
                  // No query string — keep just the trailing empty row, reusing its _id.
                  const trailingId =
                    currentParams.find((r) => r.key === '')?._id ?? makeKvRowId('p');
                  newParamRows = [{ key: '', value: '', enabled: true, _id: trailingId }];
                } else {
                  const filledRows = parsed.map((p, i) => ({
                    key: p.key,
                    value: p.value ?? '',
                    enabled: true,
                    _id: currentParams[i]?._id ?? makeKvRowId('p'),
                  }));
                  const trailingId =
                    currentParams[filledRows.length]?._id ?? makeKvRowId('p');
                  newParamRows = [
                    ...filledRows,
                    { key: '', value: '', enabled: true, _id: trailingId },
                  ];
                }
                setParams(newParamRows);
                emitDataChange({
                  name,
                  method,
                  url: value,
                  headers,
                  params: newParamRows,
                  cookies, body, bodyType, auth,
                });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
                  sendHttpRequest();
                }
              }}
              onPastePlainText={handleUrlPaste}
              forceSyncNonce={urlImportNonce}
              className="url-input request-url-bar__url-input border-0 rounded-none focus:outline-none min-h-0 bg-transparent"
              style={{ minHeight: '36px' }}
              selectedEnvironment={selectedEnvironment}
              workspaceVariables={workspaceVariables}
              globalVariables={globalVariables}
              collectionVariables={collectionVariables}
              folderVariables={folderVariables}
              onVariableUpdate={onVariableUpdate}
              variableContext={{ collectionId: initialData?.collectionId, workspaceId: currentWorkspaceId }}
            />
          </div>
    
          <button
            ref={historyDropdownTriggerRef}
            type="button"
            onClick={() => setShowHistoryDropdown((v) => !v)}
            className="request-url-bar__history"
            title={t('request.history')}
            aria-label={t('request.history')}
          >
            <Clock size={15} />
          </button>
        </div>
    
        {showHistoryDropdown && historyDropdownPosition && (
          <div className="req-history-popover" style={historyDropdownPosition} ref={historyDropdownRef}>
            <div className="req-history-popover__header">
              <span>{t('request.history')}</span>
              {requestHistory.length > 0 && (
                <button
                  type="button"
                  className="req-history-popover__clear"
                  onClick={() => onClearRequestHistory?.(requestHistoryKey)}
                  title={t('sidebar.clearHistory')}
                  aria-label={t('sidebar.clearHistory')}
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
            {requestHistory.length === 0 ? (
              <div className="req-history-popover__empty">{t('request.noHistoryForRequest')}</div>
            ) : (
              <ul className="req-history-popover__list">
                {requestHistory.map((item, i) => {
                  const isCurrent = i === 0; // newest run = the "Current" response
                  const active = isCurrent
                    ? (activeHistoryId === 'current' || activeHistoryId === item.id)
                    : (activeHistoryId === item.id);
                  if (isCurrent) {
                    return (
                      <li key={item.id ?? item.timestamp ?? i}>
                        <button
                          type="button"
                          className={`req-history-popover__item${active ? ' req-history-popover__item--active' : ''}`}
                          onClick={exitHistoryPreview}
                        >
                          <span className="req-history-popover__current">{t('request.current')}</span>
                        </button>
                      </li>
                    );
                  }
                  const status = item.status;
                  const statusTone =
                    status == null ? 'none'
                      : status >= 500 ? 'err'
                        : status >= 400 ? 'warn'
                          : status >= 300 ? 'redir'
                            : status >= 200 ? 'ok' : 'none';
                  return (
                    <li key={item.id ?? item.timestamp ?? i}>
                      <button
                        type="button"
                        className={`req-history-popover__item${active ? ' req-history-popover__item--active' : ''}`}
                        onClick={() => enterHistoryPreview(item)}
                      >
                        <span className={`req-history-popover__status req-history-popover__status--${statusTone}`}>
                          {status ?? '—'}
                        </span>
                        <span className="req-history-popover__meta">
                          {item.responseTime != null ? `${item.responseTime} ms` : ''}
                        </span>
                        <span className="req-history-popover__time">
                          {formatHistoryTime(item.timestamp, timeFormat)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
    
        <div className="request-url-bar__send-group flex flex-row items-stretch flex-shrink-0 self-stretch rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={isEventStreaming ? () => stopHttpEventStream(initialData?.id) : sendHttpRequest}
            disabled={!url || loading}
            className={`request-url-bar__send${isEventStreaming ? ' stream-bar__action--danger' : ''} inline-flex items-center px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-semibold border-0 rounded-none`}
          >
            {loading ? (
              <span className="request-url-bar__send-spinner" role="status" aria-label={t('request.sending')} />
            ) : (
              <span>{isEventStreaming ? t('request.stopStream') : t('request.send')}</span>
            )}
          </button>
          <div className="w-px bg-blue-500/40 self-stretch flex-shrink-0" aria-hidden />
          <button
            ref={sendDropdownTriggerRef}
            type="button"
            onClick={() => setShowSendDropdown((v) => !v)}
            disabled={!url || loading || isEventStreaming}
            className={`inline-flex items-center justify-center px-2 py-1.5 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border-0 rounded-none self-stretch${isEventStreaming ? ' stream-bar__action--danger' : ''}`}
            title={t('request.sendOptions')}
            aria-label={t('request.sendOptions')}
          >
            <ChevronDown size={14} strokeWidth={2.5} />
          </button>
        </div>
        {showSendDropdown && sendDropdownPosition && (
          <div className="acct-menu" style={sendDropdownPosition} ref={sendDropdownRef}>
            <nav className="acct-menu__nav">
              <button
                type="button"
                className="acct-menu__item"
                onClick={downloadResponse}
              >
                <div className="acct-menu__icon-box"><Download size={13} /></div>
                <div className="acct-menu__item-text">
                  <div className="acct-menu__item-title">Download</div>
                </div>
              </button>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
}
